import { useEffect, useMemo, useState } from 'react';

/**
 * اختيار متدرج: اللون أولاً، وبعده كل خاصية بتظهر بس لو موجودة في متغيرات
 * الاختيارات اللي قبلها، وبقيمها الموجودة فقط.
 * لكل خاصية: القيمة المفضلة لو موجودة (زي تركيبة محفوظة في المفضلة)، وإلا أول قيمة.
 */
export const buildSelection = (options, variants, preferred) => {
  let pool = variants;
  const chosen = {};
  const visible = [];

  for (const option of options) {
    const stepPool = pool;
    const present = option.values.filter((value) => stepPool.some((variant) => variant.value_ids.includes(value.id)));
    if (present.length === 0) continue;

    const preferredId = present.some((value) => value.id === preferred[option.attribute_id]) ? preferred[option.attribute_id] : null;
    const pick = preferredId ?? present[0].id;

    chosen[option.attribute_id] = pick;
    visible.push({ ...option, values: present });
    pool = pool.filter((variant) => variant.value_ids.includes(pick));
  }

  const count = Object.keys(chosen).length;
  const variant = pool.find((v) => v.value_ids.length === count) ?? (pool.length === 1 ? pool[0] : null);

  return { chosen, visible, variant };
};

// قيم تركيبة معينة كاختيار مبدئي: { [attribute_id]: value_id }
export const preferredFromVariant = (options, variants, variantId) => {
  const variant = variantId && variants.find((v) => v.id === Number(variantId));
  if (!variant) return {};
  return Object.fromEntries(
    options
      .map((option) => [option.attribute_id, option.values.find((value) => variant.value_ids.includes(value.id))?.id])
      .filter(([, valueId]) => valueId)
  );
};

/**
 * صور اللون المختار: صور اللون نفسه أولاً، وبعدها الصور اللي ما إلها لون
 * (عامة). لو اللون ما إله صور، بنرجع كل الصور عشان الكارت ما يفضى.
 */
export const imagesForColor = (images, colorId) => {
  if (!colorId) return images;
  const own = images.filter((image) => image.color_id === colorId);
  if (own.length === 0) return images;
  return [...own, ...images.filter((image) => !image.color_id)];
};

/**
 * اختيار اللون والخصائص لمنتج (options/variants من ProductResource).
 * يرجع الخصائص الظاهرة حسب الاختيار الحالي، والمتغير المطابق بسعره.
 * initialVariantId: يفتح المنتج على تركيبة معينة (من المفضلة أو السلة).
 */
export const useVariantSelection = (product, initialVariantId = null) => {
  const allOptions = useMemo(() => product?.options || [], [product]);
  const variants = useMemo(() => product?.variants || [], [product]);
  const [preferred, setPreferred] = useState(() => preferredFromVariant(allOptions, variants, initialVariantId));

  useEffect(() => {
    setPreferred(preferredFromVariant(allOptions, variants, initialVariantId));
  }, [allOptions, variants, initialVariantId]);

  const { chosen, visible, variant } = useMemo(
    () => buildSelection(allOptions, variants, preferred),
    [allOptions, variants, preferred]
  );

  // عند تغيير خاصية: نثبّت اللي قبلها، والخصائص اللي بعدها ترجع لأول قيمة
  const selectValue = (attributeId, valueId) => {
    const next = {};
    for (const option of visible) {
      if (option.attribute_id === attributeId) {
        next[attributeId] = valueId;
        break;
      }
      next[option.attribute_id] = chosen[option.attribute_id];
    }
    setPreferred(next);
  };

  // وصف التركيبة المختارة، مثلاً "أسود"
  const selectedLabel = visible
    .map((option) => option.values.find((value) => value.id === chosen[option.attribute_id])?.value)
    .filter(Boolean)
    .join(' / ');

  const colorOption = visible.find((option) => option.is_color);
  const selectedColorId = colorOption ? chosen[colorOption.attribute_id] : null;
  const images = useMemo(() => imagesForColor(product?.images || [], selectedColorId), [product, selectedColorId]);

  return { options: visible, variants, selected: chosen, selectedVariant: variant, selectedLabel, selectValue, images };
};
